import { useState, useEffect, useCallback } from 'react';
import { HeartPulse, TrendingDown, AlertTriangle, BarChart3 } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

interface ComponentScores {
  survey_score: number;
  training_completion_rate: number;
  review_participation: number;
  portal_activity_score: number;
  oneonone_cadence_score: number;
}

interface EngagementScore {
  id: string;
  employee_id?: string;
  department_id?: string;
  department_name?: string;
  score: number;
  component_scores: ComponentScores;
  snapshot_week: string;
}

interface TrendPoint {
  snapshot_week: string;
  score: number;
  department_name?: string;
}

interface HeatmapRow {
  department_id: string;
  department_name: string;
  scores: Array<number | null>;
}

interface ScoresData {
  org_score: EngagementScore | null;
  department_scores: EngagementScore[];
  current_week: string;
}

interface TrendsData {
  org_trend: TrendPoint[];
  dept_trend: TrendPoint[];
}

interface HeatmapData {
  weeks: string[];
  rows: HeatmapRow[];
}

// ─── Helpers ─────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 70) return '#22c55e';
  if (score >= 50) return '#f59e0b';
  if (score >= 30) return '#f97316';
  return '#ef4444';
}

function scoreTextColor(score: number): string {
  if (score >= 70) return 'text-green-400';
  if (score >= 50) return 'text-amber-400';
  if (score >= 30) return 'text-orange-400';
  return 'text-red-400';
}

// ─── Large Ring Gauge (org score) ────────────────────────────

function OrgRingGauge({ score }: { score: number }) {
  const r = 70;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference - (score / 100) * circumference;
  const color = scoreColor(score);
  const label = score >= 70 ? 'Engaged' : score >= 50 ? 'Moderate' : score >= 30 ? 'At Risk' : 'Critical';

  return (
    <div className="flex flex-col items-center">
      <svg width="168" height="168" viewBox="0 0 168 168">
        <circle cx="84" cy="84" r={r} fill="none" stroke="#27272a" strokeWidth="12" />
        <circle
          cx="84" cy="84" r={r} fill="none"
          stroke={color} strokeWidth="12"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform="rotate(-90 84 84)"
        />
        <text x="84" y="80" textAnchor="middle" fontSize="28" fontWeight="bold" fill={color}>{score}</text>
        <text x="84" y="98" textAnchor="middle" fontSize="12" fill="#a1a1aa">{label}</text>
      </svg>
      <p className="text-xs text-muted-foreground mt-1">Org Engagement Score</p>
    </div>
  );
}

// ─── 13-Week Trend SVG Line Chart ────────────────────────────

function TrendChart({ orgTrend, deptTrend, weeks }: {
  orgTrend: TrendPoint[];
  deptTrend: TrendPoint[];
  weeks: string[];
}) {
  const w = 560;
  const h = 120;
  const padding = { left: 30, right: 10, top: 10, bottom: 20 };
  const plotW = w - padding.left - padding.right;
  const plotH = h - padding.top - padding.bottom;
  const n = weeks.length;
  if (n < 2) return <div className="h-24 flex items-center justify-center text-xs text-muted-foreground">Not enough data</div>;

  const toX = (i: number) => padding.left + (i / (n - 1)) * plotW;
  const toY = (score: number) => padding.top + plotH - (score / 100) * plotH;

  const buildPath = (points: TrendPoint[]) => {
    const mapped = weeks.map(w => points.find(p => p.snapshot_week === w));
    return mapped
      .map((p, i) => p ? `${i === 0 || !mapped[i - 1] ? 'M' : 'L'}${toX(i)},${toY(p.score)}` : null)
      .filter(Boolean)
      .join(' ');
  };

  const orgPath = buildPath(orgTrend);
  const deptPath = deptTrend.length > 0 ? buildPath(deptTrend) : null;

  // Y-axis ticks
  const yTicks = [0, 25, 50, 75, 100];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32">
      {/* Y-axis */}
      {yTicks.map(tick => (
        <g key={tick}>
          <line x1={padding.left} y1={toY(tick)} x2={w - padding.right} y2={toY(tick)} stroke="#27272a" strokeWidth="0.5" strokeDasharray="3,3" />
          <text x={padding.left - 4} y={toY(tick) + 3} textAnchor="end" fontSize="8" fill="#71717a">{tick}</text>
        </g>
      ))}
      {/* Org line */}
      {orgPath && <path d={orgPath} fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
      {/* Dept overlay */}
      {deptPath && <path d={deptPath} fill="none" stroke="#6366f1" strokeWidth="1.5" strokeDasharray="4,2" strokeLinecap="round" />}
      {/* Week labels — show every other */}
      {weeks.map((wk, i) => i % 2 === 0 ? (
        <text key={wk} x={toX(i)} y={h - 4} textAnchor="middle" fontSize="7" fill="#71717a">{wk.slice(5)}</text>
      ) : null)}
    </svg>
  );
}

// ─── Department Heatmap ───────────────────────────────────────

function DepartmentHeatmap({ weeks, rows }: HeatmapData) {
  if (rows.length === 0 || weeks.length === 0) {
    return <div className="text-xs text-muted-foreground p-4 text-center">No heatmap data available yet</div>;
  }

  const cellW = Math.floor(Math.min(40, 480 / weeks.length));
  const cellH = 28;
  const labelW = 140;
  const headerH = 20;
  const totalW = labelW + weeks.length * cellW + 10;
  const totalH = headerH + rows.length * cellH + 10;

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${totalW} ${totalH}`} className="w-full" style={{ minWidth: totalW }}>
        {/* Week headers */}
        {weeks.map((wk, j) => (
          <text key={wk} x={labelW + j * cellW + cellW / 2} y={14} textAnchor="middle" fontSize="7" fill="#71717a">{wk.slice(5)}</text>
        ))}
        {/* Rows */}
        {rows.map((row, i) => (
          <g key={row.department_id}>
            <text x={labelW - 5} y={headerH + i * cellH + cellH / 2 + 4} textAnchor="end" fontSize="9" fill="#a1a1aa">{row.department_name}</text>
            {weeks.map((_, j) => {
              const score = row.scores[j] ?? null;
              const fill = score !== null ? scoreColor(score) + '55' : '#18181b';
              const strokeColor = score !== null ? scoreColor(score) : '#27272a';
              return (
                <g key={j}>
                  <rect
                    x={labelW + j * cellW + 1}
                    y={headerH + i * cellH + 2}
                    width={cellW - 2}
                    height={cellH - 4}
                    rx="2"
                    fill={fill}
                    stroke={strokeColor}
                    strokeWidth="0.5"
                    opacity={score !== null ? 1 : 0.3}
                  />
                  {score !== null && (
                    <text
                      x={labelW + j * cellW + cellW / 2}
                      y={headerH + i * cellH + cellH / 2 + 4}
                      textAnchor="middle"
                      fontSize="8"
                      fill={strokeColor}
                      fontWeight="600"
                    >
                      {score}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        ))}
      </svg>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

type Tab = 'overview' | 'trends' | 'heatmap';

export function EngagementScorePage() {
  const [scoresData, setScoresData] = useState<ScoresData | null>(null);
  const [trendsData, setTrendsData] = useState<TrendsData | null>(null);
  const [heatmapData, setHeatmapData] = useState<HeatmapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('overview');
  const [selectedDept, setSelectedDept] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [scoresRes, trendsRes, heatmapRes] = await Promise.all([
        fetch('/api/people/engagement/scores'),
        fetch(`/api/people/engagement/trends${selectedDept ? `?department_id=${selectedDept}` : ''}`),
        fetch('/api/people/engagement/heatmap'),
      ]);
      if (scoresRes.ok) setScoresData(await scoresRes.json() as ScoresData);
      if (trendsRes.ok) setTrendsData(await trendsRes.json() as TrendsData);
      if (heatmapRes.ok) setHeatmapData(await heatmapRes.json() as HeatmapData);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [selectedDept]);

  useEffect(() => { void load(); }, [load]);

  async function refresh() {
    setRefreshing(true);
    try {
      await fetch('/api/people/engagement/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      await load();
    } catch { /* silent */ }
    finally { setRefreshing(false); }
  }

  const orgScore = scoresData?.org_score?.score ?? null;
  const deptScores = scoresData?.department_scores ?? [];
  const allWeeks = trendsData?.org_trend.map(p => p.snapshot_week) ?? [];

  // Bottom 5 lowest engagement departments for alert feed
  const sortedDepts = [...deptScores].sort((a, b) => a.score - b.score);
  const lowestDepts = sortedDepts.slice(0, 5);

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <HeartPulse className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold text-foreground">Engagement Scores</h1>
        </div>
        <button
          onClick={() => void refresh()}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-surface-3 disabled:opacity-50"
        >
          {refreshing ? 'Recalculating...' : 'Recalculate Now'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1">
        {(['overview', 'trends', 'heatmap'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${tab === t ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground hover:bg-surface-3'}`}>
            {t === 'heatmap' ? 'Department Heatmap' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading...</div>
      ) : tab === 'overview' ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Org gauge */}
            <div className="glass rounded-xl p-6 flex items-center justify-center">
              {orgScore !== null ? (
                <OrgRingGauge score={orgScore} />
              ) : (
                <div className="text-center text-muted-foreground text-sm">
                  <HeartPulse className="w-10 h-10 opacity-20 mx-auto mb-2" />
                  No scores yet — click Recalculate
                </div>
              )}
            </div>

            {/* Component breakdown */}
            {scoresData?.org_score && (
              <div className="glass rounded-xl p-5">
                <h3 className="text-sm font-semibold text-foreground mb-4">Score Components</h3>
                <div className="space-y-3">
                  {Object.entries(scoresData.org_score.component_scores).map(([key, val]) => {
                    const label = key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    return (
                      <div key={key}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">{label}</span>
                          <span className={`font-semibold ${scoreTextColor(val)}`}>{val}</span>
                        </div>
                        <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${val}%`, backgroundColor: scoreColor(val) }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Department scores */}
          {deptScores.length > 0 && (
            <div className="glass rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">Department Scores</h3>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-5 py-2.5 font-medium text-muted-foreground">Department</th>
                    <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">Score</th>
                    <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">Week</th>
                  </tr>
                </thead>
                <tbody>
                  {deptScores.map(d => (
                    <tr key={d.id} className="border-b border-border/50 hover:bg-surface-3/50">
                      <td className="px-5 py-2.5 text-foreground">{d.department_name}</td>
                      <td className="text-center px-4 py-2.5">
                        <span className={`font-bold ${scoreTextColor(d.score)}`}>{d.score}</span>
                      </td>
                      <td className="text-center px-4 py-2.5 text-muted-foreground">{d.snapshot_week}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Alert feed: lowest 5 */}
          {lowestDepts.filter(d => d.score < 50).length > 0 && (
            <div className="glass rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <h3 className="text-sm font-semibold text-foreground">Engagement Alerts</h3>
              </div>
              <div className="space-y-2">
                {lowestDepts.filter(d => d.score < 50).map(d => (
                  <div key={d.id} className="flex items-center justify-between p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                      <span className="text-sm text-foreground">{d.department_name}</span>
                    </div>
                    <span className="text-sm font-bold text-red-400">{d.score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : tab === 'trends' ? (
        <div className="space-y-4">
          {/* Dept selector */}
          {deptScores.length > 0 && (
            <div className="flex items-center gap-3">
              <label className="text-xs text-muted-foreground">Department overlay:</label>
              <select
                value={selectedDept}
                onChange={e => setSelectedDept(e.target.value)}
                className="bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              >
                <option value="">None</option>
                {deptScores.map(d => (
                  <option key={d.department_id} value={d.department_id ?? ''}>{d.department_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="glass rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-semibold text-foreground">13-Week Engagement Trend</h3>
            </div>
            <TrendChart
              orgTrend={trendsData?.org_trend ?? []}
              deptTrend={trendsData?.dept_trend ?? []}
              weeks={allWeeks}
            />
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-0.5 bg-amber-400" />
                <span className="text-xs text-muted-foreground">Org Average</span>
              </div>
              {selectedDept && trendsData && trendsData.dept_trend.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-0.5 bg-indigo-400" style={{ borderTop: '1px dashed' }} />
                  <span className="text-xs text-muted-foreground">Department</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Heatmap */
        heatmapData && (
          <div className="glass rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Department × Week Heatmap</h3>
            <DepartmentHeatmap weeks={heatmapData.weeks} rows={heatmapData.rows} />
            <div className="flex items-center gap-4 mt-3">
              <span className="text-xs text-muted-foreground">Color scale:</span>
              {[['70+', '#22c55e'], ['50-69', '#f59e0b'], ['30-49', '#f97316'], ['<30', '#ef4444']].map(([label, color]) => (
                <div key={label} className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: color + '55', border: `1px solid ${color}` }} />
                  <span className="text-xs text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
}
